import type { SavedCatalogFilter } from '~/types'
import { decodeEntities } from '~/utils/html'

/**
 * The manager toolbar's "Filtrų ruošiniai" list (`e_customer_filters`).
 *
 * A store rather than component state because both toolbar controls read it: the
 * dropdown lists the presets and the save button prepends to the same list, and
 * the two are separate components. The list is fetched once per session, lazily —
 * the toolbar only exists for managers, so nothing is requested for an ordinary
 * customer, and nothing is requested until the dropdown is first opened.
 */
export const useSavedCatalogFilters = defineStore('savedCatalogFilters', () => {
  const filters = ref<SavedCatalogFilter[]>([])
  const loading = ref(false)
  const loaded = ref(false)
  const error = ref<string | null>(null)

  /**
   * The rows come out of the admin and out of the legacy Smarty catalogue, both of
   * which stored their text HTML-escaped — a saved url reads
   * `...?search=andora&amp;filter=fmod_4:3078`, which no router will resolve. The
   * whole row goes through the same decode the menus use.
   */
  function normalize(filter: SavedCatalogFilter): SavedCatalogFilter {
    return { ...filter, name: decodeEntities(filter.name), url: decodeEntities(filter.url) }
  }

  /** The backend's own wording out of a thrown ofetch error, when there is one. */
  function apiMessage(e: unknown): string | null {
    const body = (e as { data?: unknown, response?: { _data?: unknown } })?.data
      ?? (e as { response?: { _data?: unknown } })?.response?._data
    const message = (body as { message?: unknown })?.message
    return typeof message === 'string' ? message : null
  }

  async function fetchFilters(force = false) {
    if (loading.value) return
    if (loaded.value && !force) return

    loading.value = true
    error.value = null
    try {
      const res = await useApi().getSavedFilters()
      if (!res.success) {
        error.value = res.message ?? null
        return
      }
      filters.value = (res.data.filters ?? []).map(normalize)
      loaded.value = true
    }
    catch (e) {
      error.value = apiMessage(e)
    }
    finally {
      loading.value = false
    }
  }

  /**
   * Saves the url the caller passes — a site-relative path plus its query, never
   * `window.location.href`: the column is 255 characters and the rows outlive the
   * host they were saved on (the legacy Smarty rows already hold a mix of
   * `nostra.multishop.lt` and `nostra-b2b.dev.elab.lt` urls).
   */
  async function save(name: string, url: string): Promise<SavedCatalogFilter | null> {
    // The new preset joins the REAL list, not whatever happens to be in memory.
    //
    // The list is fetched lazily, on the dropdown's first open — and saving is
    // reachable without ever opening it, from the button beside it. Appending to
    // an empty `filters` and marking the store loaded then left the manager with
    // a preset list holding only the row they had just saved, and a
    // `fetchFilters()` that short-circuited for the rest of the session: every
    // other preset, their own and the shared ones, vanished until a reload.
    //
    // A failed fetch is not a reason to refuse the save: it leaves `loaded`
    // false, so the list simply fetches again on the next open.
    await fetchFilters()

    loading.value = true
    error.value = null
    try {
      const res = await useApi().saveCatalogFilter(name, url)
      if (!res.success) {
        error.value = res.message ?? null
        return null
      }
      const filter = normalize(res.data.filter)
      filters.value = [...filters.value, filter]
      return filter
    }
    catch (e) {
      error.value = apiMessage(e)
      return null
    }
    finally {
      loading.value = false
    }
  }

  async function remove(id: number): Promise<boolean> {
    loading.value = true
    error.value = null
    try {
      const res = await useApi().deleteSavedFilter(id)
      if (!res.success) {
        error.value = res.message ?? null
        return false
      }
      filters.value = filters.value.filter(filter => filter.id !== id)
      return true
    }
    catch (e) {
      error.value = apiMessage(e)
      return false
    }
    finally {
      loading.value = false
    }
  }

  function reset() {
    filters.value = []
    loaded.value = false
    error.value = null
  }

  return { filters, loading, loaded, error, fetchFilters, save, remove, reset }
})
